import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AjaxService } from '../../../service/ajax.service';
import { UtilService } from '../../../service/util.service';
import { API } from '../../../../environments/environment';

export interface Cliente {
  // Campos principais do PacienteClinica (lowercase)
  id_paciente_clinica?: number;
  id_empresa?: number;
  id_usuario?: number;
  email?: string;
  nome?: string;
  data_cadastro?: Date;
  cpf?: string;
  data_nascimento?: Date;
  id_cidade?: number;
  rua?: string;
  bairro?: string;
  complemento?: string;
  cep?: string;
  numero?: string;
  telefone_residencial?: string;
  telefone_celular?: string;
  rg?: string;
  carteira?: string;
  dante_causa?: string;
  genero?: number;
  telefone_adicional?: string;
  observacoes?: string;
  nome_mae?: string;
  nome_pai?: string;
  arvore?: string;
  link_pasta?: string;
  apelido_pasta_virtual?: string;
  sharepoint?: string;
  pasta_fisica?: boolean;
  
  // Campos NotMapped para formulário (lowercase)
  senha?: string;
  senha_confirmacao?: string;
  ativo?: boolean;
  
  // Campos adicionais para interface
  pais?: string;
  estado?: string;
  cidade?: string;
}

@Component({
  selector: 'app-modal-cadastro-cliente',
  templateUrl: './modal-cadastro-cliente.component.html',
  styleUrls: ['./modal-cadastro-cliente.component.scss']
})
export class ModalCadastroClienteComponent implements OnInit, OnChanges {

  @Input() isVisible: boolean = false;
  @Input() clienteEdicao: any = null;
  @Output() modalClosed = new EventEmitter<boolean>();
  @Output() clienteSalvo = new EventEmitter<any>();

  formCliente: FormGroup;
  isEditando: boolean = false;

  constructor(
    private fb: FormBuilder,
    private ajaxService: AjaxService,
    private util: UtilService
  ) {
    this.formCliente = this.criarFormulario();
  }

  ngOnInit(): void {
    // Inicialização básica do componente
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('ngOnChanges chamado:', changes);
    
    if (changes['clienteEdicao']) {
      if (changes['clienteEdicao'].currentValue) {
        console.log('Cliente para edição recebido:', changes['clienteEdicao'].currentValue);
        this.isEditando = true;
        // Usar setTimeout para garantir que o formulário esteja pronto
        setTimeout(() => {
          this.preencherFormulario();
        }, 0);
      } else {
        this.isEditando = false;
      }
    }
    
    if (changes['isVisible'] && changes['isVisible'].currentValue && !this.clienteEdicao) {
      // Modal aberto para novo cliente
      console.log('Modal aberto para novo cliente');
      this.isEditando = false;
      this.limparFormulario();
    }
  }

  criarFormulario(): FormGroup {
    return this.fb.group({
      // Campos principais (nomes em lowercase)
      nome: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      cpf: ['', [Validators.required, Validators.pattern(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/)]],
      senha: ['', [Validators.required, Validators.minLength(6)]],
      
      // Telefones
      telefone_celular: ['', [Validators.required]],
      telefone_adicional: [''],
      
      // Endereço
      rua: ['', [Validators.required]],
      numero: ['', [Validators.required]],
      complemento: [''],
      bairro: ['', [Validators.required]],
      cep: ['', [Validators.required, Validators.pattern(/^\d{5}-\d{3}$/)]],
      pais: ['', [Validators.required]], // Campo auxiliar para interface
      estado: ['', [Validators.required]], // Campo auxiliar para interface
      cidade: ['', [Validators.required]], // Campo auxiliar para interface
      
      // Informações adicionais
      link_pasta: [''],
      apelido_pasta_virtual: [''],
      pasta_fisica: [false],
      observacoes: ['']
    });
  }


  preencherFormulario(): void {
    console.log('Preenchendo formulário com dados:', this.clienteEdicao);
    
    if (this.clienteEdicao) {
      const formData = {
        // Campos principais
        nome: this.clienteEdicao.nome || '',
        email: this.clienteEdicao.email || '',
        cpf: this.clienteEdicao.cpf || '',
        senha: this.clienteEdicao.senha || '',
        
        // Telefones
        telefone_celular: this.clienteEdicao.telefone_celular || '',
        telefone_adicional: this.clienteEdicao.telefone_adicional || '',
        
        // Endereço
        rua: this.clienteEdicao.rua || '',
        numero: this.clienteEdicao.numero || '',
        complemento: this.clienteEdicao.complemento || '',
        bairro: this.clienteEdicao.bairro || '',
        cep: this.clienteEdicao.cep || '',
        cidade: this.clienteEdicao.cidade || '',
        estado: this.clienteEdicao.estado || '',
        pais: this.clienteEdicao.pais || '',
        
        // Informações adicionais
        link_pasta: this.clienteEdicao.link_pasta || '',
        apelido_pasta_virtual: this.clienteEdicao.apelido_pasta_virtual || '',
        pasta_fisica: this.clienteEdicao.pasta_fisica || false,
        observacoes: this.clienteEdicao.observacoes || ''
      };
      
      console.log('Dados do formulário:', formData);
      this.formCliente.patchValue(formData);
      console.log('Formulário após patchValue:', this.formCliente.value);
    }
  }

  fecharModal(): void {
    this.isVisible = false;
    this.modalClosed.emit(false);
    this.limparFormulario();
  }

  limparFormulario(): void {
    this.formCliente.reset();
    this.isEditando = false;
    this.clienteEdicao = null;
  }

  salvarCliente(): void {
    if (this.formCliente.valid) {
      const formValue = this.formCliente.value;
      
      // Mapear dados do formulário para o objeto Cliente (lowercase)
      const dadosCliente: Cliente = {
        // Campos principais
        nome: formValue.nome,
        email: formValue.email,
        cpf: formValue.cpf,
        senha: formValue.senha,
        
        // Telefones
        telefone_celular: formValue.telefone_celular,
        telefone_adicional: formValue.telefone_adicional,
        
        // Endereço
        rua: formValue.rua,
        numero: formValue.numero,
        complemento: formValue.complemento,
        bairro: formValue.bairro,
        cep: formValue.cep,
        
        // Informações adicionais
        link_pasta: formValue.link_pasta,
        apelido_pasta_virtual: formValue.apelido_pasta_virtual,
        pasta_fisica: formValue.pasta_fisica,
        observacoes: formValue.observacoes,
        
        // Campos de sistema
        data_cadastro: new Date()
      };

      // Se estiver editando, manter o ID
      if (this.isEditando && this.clienteEdicao?.id_paciente_clinica) {
        dadosCliente.id_paciente_clinica = this.clienteEdicao.id_paciente_clinica;
        dadosCliente.id_empresa = this.clienteEdicao.id_empresa;
        dadosCliente.id_usuario = this.clienteEdicao.id_usuario;
      }
      
      let url = API + "empresa/paciente/Save";
      if (this.isEditando) {
        url = API + "empresa/Paciente/AtualizarCliente";
      }

      this.util.showLoading();
      this.ajaxService.post(url, dadosCliente).subscribe({
        next: (response) => {
          this.util.closeLoading();
          this.util.toasterShowSuccess('Sucesso', 
            this.isEditando ? 'Cliente atualizado com sucesso!' : 'Cliente cadastrado com sucesso!');
          
          this.clienteSalvo.emit(response.data || dadosCliente);
          this.fecharModal();
        },
        error: (error) => {
          this.util.closeLoading();
          console.error('Erro ao salvar cliente:', error);
          this.util.toasterShowError('Erro', 'Erro ao salvar cliente');
        }
      });
    } else {
      this.util.toasterShowError('Erro', 'Por favor, preencha todos os campos obrigatórios');
      this.marcarCamposComoTocados();
    }
  }

  marcarCamposComoTocados(): void {
    Object.keys(this.formCliente.controls).forEach(key => {
      this.formCliente.get(key)?.markAsTouched();
    });
  }

  // Métodos para validação de campos
  isCampoInvalido(campo: string): boolean {
    const control = this.formCliente.get(campo);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getMensagemErro(campo: string): string {
    const control = this.formCliente.get(campo);
    if (control?.errors) {
      if (control.errors['required']) {
        return 'Este campo é obrigatório';
      }
      if (control.errors['email']) {
        return 'Email inválido';
      }
      if (control.errors['minlength']) {
        return `Mínimo de ${control.errors['minlength'].requiredLength} caracteres`;
      }
      if (control.errors['pattern']) {
        if (campo === 'cpf') {
          return 'CPF inválido (formato: 000.000.000-00)';
        }
        if (campo === 'cep') {
          return 'CEP inválido (formato: 00000-000)';
        }
        return 'Formato inválido';
      }
    }
    return '';
  }
}
